import React, { useState, useRef, useEffect } from "react";

/**
 * CustomAutocomplete - Autocomplete personalizado compatible con navegadores antiguos
 *
 * Props:
 * - value: objeto seleccionado o texto escrito
 * - options: array de objetos con { key, value } donde value es el texto a mostrar
 * - onChange: función callback cuando cambia el texto (recibe el evento simulado { value })
 * - onSelect: función callback cuando se selecciona una opción (recibe el evento simulado { value: objeto })
 * - placeholder: texto cuando no hay selección
 * - disabled: deshabilitar el input
 * - hasError: mostrar estado de error
 * - name: nombre del campo (para formularios)
 * - field: nombre del campo a mostrar del objeto (default: "value")
 */
const CustomAutocomplete = ({
  value,
  options = [],
  onChange,
  onSelect,
  placeholder = "Escriba para buscar...",
  disabled = false,
  hasError = false,
  name = "",
  field = "value"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Sincronizar el valor del input con el value prop
  useEffect(() => {
    if (value && typeof value === "object" && value[field]) {
      setInputValue(value[field]);
    } else if (typeof value === "string") {
      setInputValue(value);
    } else {
      setInputValue("");
    }
  }, [value, field]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Filtrar opciones
    if (newValue.trim()) {
      const query = newValue.toLowerCase();
      const filtered = options.filter((opt) =>
        opt[field] && opt[field].toLowerCase().startsWith(query)
      );
      setFilteredOptions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      setFilteredOptions([]);
      setIsOpen(false);
    }

    // Notificar cambio al padre
    if (onChange) {
      onChange({ value: newValue });
    }
  };

  const handleSelect = (option) => {
    setInputValue(option[field]);
    setIsOpen(false);
    setFilteredOptions([]);

    // Notificar selección al padre
    if (onSelect) {
      onSelect({ value: option });
    }
  };

  const handleFocus = () => {
    if (inputValue.trim() && filteredOptions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Clases CSS
  const containerClass = [
    "custom-autocomplete-container",
    disabled ? "custom-autocomplete-disabled" : "",
    hasError ? "custom-autocomplete-error shake" : "",
    isOpen ? "custom-autocomplete-open" : ""
  ].filter(Boolean).join(" ");

  const inputClass = [
    "custom-autocomplete-input",
    "form-control",
    hasError ? "is-invalid" : ""
  ].filter(Boolean).join(" ");

  return (
    <div className={containerClass} ref={containerRef}>
      {/* Input de texto */}
      <input
        ref={inputRef}
        type="text"
        className={inputClass}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        name={name}
      />

      {/* Dropdown de sugerencias */}
      {isOpen && filteredOptions.length > 0 && (
        <div className="custom-autocomplete-dropdown">
          <ul className="custom-autocomplete-options">
            {filteredOptions.map((option, index) => (
              <li
                key={option.key || index}
                className="custom-autocomplete-option"
                onClick={() => handleSelect(option)}
              >
                {option[field]}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomAutocomplete;
